import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { Gender } from '@common/enums';
import type { PatientUnavailableServices } from '@common/interfaces/patientUnavailableServices';
import { FoundPatientStatus } from '@common/enums';
import { useAbortableFetch } from '@frontend/utils';
import type { IPatientsListFilters } from '../Patients';
import type { NewPatient } from './patientSearchContext';
import type { IPatientSearchResult } from './PatientSearchStartBar';
import type { IPatientSearchInformation } from './searchPatientFunctions';
import { emptyPatient } from './emptyPatient';
import { PatientSearchContext, SearchType } from './patientSearchContext';
import {
    findPatientInKsyos,
    findPatientInSbvzAndVecozo,
    findPatientsInOrganization,
    findUnavailableServices,
} from './searchPatientFunctions';
import { normalizePatient } from './utils';

export interface IPatientInOrganization {
    birthDate: Date;
    diagnosticResultsCount: number;
    gender: Gender;
    id: string;
    identifierInsurance: string;
    identifierSocialSecurity: string;
    lastNewInfoDateTime?: string;
    nameFamily: string;
    nameFamily2: string;
    nameGiven: string;
    nameInfix: string;
    nameInfix2: string;
    nameInitials: string;
    nameUsual: string;
    updated_at: string | Date;
    zip: string;
}

export interface IPatientSearchData {
    birthDate: string | null;
    identifierSocialSecurity: string | null;
    name: string | null;
}

interface IPatientSearch {
    children: ReactNode;
    patientsListFilters?: IPatientsListFilters;
}

export function PatientSearch(props: IPatientSearch) {
    const abortableFetch = useAbortableFetch();
    const [patient, setPatient] = useState<IPatientSearchResult>(emptyPatient());
    const [newPatient, setNewPatient] = useState<NewPatient>(null);
    const [foundPatientStatus, setFoundPatientStatus] = useState<FoundPatientStatus>(FoundPatientStatus.noSearch);
    const [patients, setPatients] = useState<(IPatientSearchResult | IPatientInOrganization)[]>([]);
    const [total, setTotal] = useState(0);
    const [message, setMessage] = useState<string>();
    const [isSearching, setIsSearching] = useState(false);
    const [unavailableServices, setUnavailableServices] = useState<PatientUnavailableServices>({});
    const [duplicatePatientId, setDuplicatePatientId] = useState<string>();

    useEffect(() => {
        loadUnavailableServices();
    }, [patients, newPatient]);

    return (
        <PatientSearchContext.Provider
            value={{
                duplicatePatientId,
                foundPatientStatus,
                isSearching,
                message,
                newPatient,
                patient,
                patients,
                search,
                setDuplicatePatientId,
                setFoundPatientStatus,
                setNewPatient,
                setPatient,
                setPatients,
                setUnavailableServices,
                total,
                unavailableServices,
            }}
        >
            {props.children}
        </PatientSearchContext.Provider>
    );

    async function search(type: SearchType, updatedPatient?: IPatientSearchResult) {
        const patientInformationToSearch = updatedPatient ?? patient;

        if (type === SearchType.extraInformationBar) {
            await searchPatientExtraInformationBar(patientInformationToSearch);
        } else {
            await searchPatientSearchStartBar(type, patientInformationToSearch);
        }
    }

    async function searchPatientExtraInformationBar(patientInformationToSearch: IPatientSearchResult) {
        await searchPatientInKsyosOrOutsideOrganization(patientInformationToSearch);
    }

    async function searchPatientSearchStartBar(type: SearchType, patientInformationToSearch: IPatientSearchResult) {
        const name =
            patientInformationToSearch.nameFamily ||
            patientInformationToSearch.nameFamily2 ||
            patientInformationToSearch.nameGiven ||
            patientInformationToSearch.nameUsual;

        if (
            !patientInformationToSearch.processNumber &&
            !patientInformationToSearch.identifierSocialSecurity &&
            !patientInformationToSearch.birthDate &&
            !name
        ) {
            return;
        }
        const searchData = {
            birthDate: patientInformationToSearch.birthDate,
            identifierSocialSecurity: patientInformationToSearch.identifierSocialSecurity,
            name,
            processNumber: patientInformationToSearch.processNumber,
        };
        setIsSearching(true);

        const { patients: foundPatients } = await findPatientsInOrganization(searchData, props.patientsListFilters);

        if (type !== SearchType.startBarOnlyInOrganization && foundPatients.length === 0) {
            return await searchPatientInKsyosOrOutsideOrganization(searchData);
        }

        if (foundPatients.length === 1) {
            setMessage(undefined);
        }

        setPatients(foundPatients);
        setTotal(foundPatients.length);
        setFoundPatientStatus(
            foundPatients.length === 0
                ? FoundPatientStatus.noPatientFound
                : FoundPatientStatus.foundPatientInOrganization
        );
        setIsSearching(false);
    }

    async function searchPatientInKsyosOrOutsideOrganization(patientInformationToSearch: IPatientSearchInformation) {
        if (!patientInformationToSearch.identifierSocialSecurity || !patientInformationToSearch.birthDate) {
            setIsSearching(false);

            return setFoundPatientStatus(FoundPatientStatus.extraInformationNeeded);
        }

        let foundPatient = await findPatientInKsyos(patientInformationToSearch);
        if (!foundPatient) {
            foundPatient = await findPatientOutsideKsyos(patientInformationToSearch);
        } else {
            setFoundPatientStatus(FoundPatientStatus.foundPatientOutsideOfOrganization);
        }

        setNewPatient(foundPatient ?? null);
        setIsSearching(false);
    }

    async function findPatientOutsideKsyos(patientInformationToSearch: IPatientSearchInformation) {
        const findPatient = await findPatientInSbvzAndVecozo(patientInformationToSearch);
        if (findPatient?.patient?.nameFamily) {
            setFoundPatientStatus(FoundPatientStatus.foundPatientOutsideOfOrganization);
            setMessage(undefined);
            findDuplicatePatientId(patient);
        } else {
            setFoundPatientStatus(FoundPatientStatus.noPatientFound);
        }

        return findPatient;
    }

    async function loadUnavailableServices() {
        const patientIds = patients
            ?.map((patientItem) => patientItem.id)
            .filter((patientId) => patientId !== undefined) as string[];

        if (newPatient?.patient?.id) {
            patientIds.push(newPatient.patient.id);
        }

        const result = await findUnavailableServices(patientIds);
        setUnavailableServices(result);
    }

    async function findDuplicatePatientId(foundPatient: IPatientSearchResult) {
        const normalizedPatient = normalizePatient(foundPatient);
        const result = await abortableFetch.fetchJson('/api/patients/duplicates', 'POST', normalizedPatient);
        setDuplicatePatientId(result.json.count > 0 ? result.json.items[0].id : undefined);
    }
}
